import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Edit, Copy, Trash2, FileDown, Building2, Clock, Home } from 'lucide-react';
import { format } from 'date-fns';

const ProjectCard = ({ project, onDelete, onDuplicate }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const statusColors = {
    draft: 'bg-amber-50 text-amber-700 border-amber-200',
    in_progress: 'bg-blue-50 text-[#1e40af] border-blue-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    archived: 'bg-slate-50 text-slate-600 border-slate-200',
  };
  
  // Professional icons mapping
  const getProjectIcon = (type) => {
    const iconMap = {
      residential_remodel: Home,
      residential_new: Home,
      tenant_improvement_food: Building2,
      tenant_improvement_retail: Building2,
      tenant_improvement_office: Building2,
      commercial_new: Building2,
    };
    return iconMap[type] || Building2;
  };
  
  const ProjectIcon = getProjectIcon(project.project_type);
  
  return (
    <Card 
      className="group hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden border border-slate-200 dark:border-slate-700 bg-white"
      data-testid={`project-card-${project.id}`}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1" onClick={() => navigate(`/project/${project.id}`)}>
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200">
                <ProjectIcon className="h-5 w-5 text-[#4a5568] dark:text-slate-300" />
              </div>
              <span className={`px-3 py-1 text-xs font-medium rounded border ${statusColors[project.status]}`}>
                {t(project.status).toUpperCase()}
              </span>
            </div>
            
            <h3 className="text-lg font-semibold text-[#1f2937] dark:text-white mb-1 tracking-tight">
              {project.name}
            </h3>
            
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
              {t(project.project_type)}
            </p>
            
            {project.address && (
              <p className="text-xs text-slate-500 flex items-center">
                <Building2 className="h-3 w-3 mr-1" />
                {project.address}
              </p>
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/project/${project.id}`)}>
                <Edit className="mr-2 h-4 w-4" />
                {t('edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(project.id, project.name)}>
                <Copy className="mr-2 h-4 w-4" />
                {t('duplicate')}
              </DropdownMenuItem>
              <DropdownMenuItem>
                <FileDown className="mr-2 h-4 w-4" />
                {t('export')}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onDelete(project.id)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center text-xs text-slate-500">
            <Clock className="h-3 w-3 mr-1" />
            {t('lastModified')}: {format(new Date(project.updated_at), 'MMM dd, yyyy')}
          </div>
          
          {project.sheets && (
            <div className="text-xs text-slate-500">
              {project.sheets.filter(s => s.included).length} {t('sheets')}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ProjectCard;